import { gql } from '@apollo/client';

export const GET_DRAFT_TOURNAMENTS_PAGINATED = gql`
  query DraftTournament($paginationOptions: PaginationOptionsInput!, $tournamentFilters: DraftTournamentFilters) {
    draftTournaments(paginationOptions: $paginationOptions, tournamentFilters: $tournamentFilters) {
      items {
        id
        name
        organisation {
          id
          urlSegment
        }
        level {
          id
          name
        }
        timings {
          startDate
          endDate
          timeZone
          startDateTime
          endDateTime
        }
        registrationRestrictions {
          entriesOpenDateTime
          entriesCloseDateTime
          timeZone
        }
        identificationCode
        approvedEventCount
        uniqueRegistrationsCount
        websiteContent {
          logoPath
        }
        primaryLocation {
          name
        }
      }
      totalItems
    }
  }
`;

export const GET_FULL_TOURNAMENT = gql`
  query FullTournament($clubSparkTournamentId: UUID!) {
    fullTournament(clubSparkTournamentId: $clubSparkTournamentId)
  }
`;

// TODO: move to mutations
export const ENTITY_MUTATION = gql`
  mutation MutateTournament($directive: TournamentMutationDirective!) {
    mutateTournament(directive: $directive) {
      success
      error {
        message
        code
      }
      newTimestamp
    }
  }
`;

export const SYNC_TOURNAMENT = gql`
  mutation SyncTournament($clubSparkTournamentId: UUID!) {
    syncWithDraftTournament(clubSparkTournamentId: $clubSparkTournamentId)
  }
`;

export const SEND_SMS = gql`
  mutation SendSMS($tournamentId: UUID!, $personIds: [UUID!]!, $message: String!) {
    sendSMS(tournamentId: $tournamentId, personIds: $personIds, message: $message) {
      personId
      sentPhoneNumbers
      failedPhoneNumbers
    }
  }
`;

export const SEND_EMAILS = gql`
  mutation SendEmails($tournamentId: UUID!, $personIds: [UUID!]!, $emailInput: SendEmailsInput!) {
    sendEmails(tournamentId: $tournamentId, personIds: $personIds, sendEmailsInput: $emailInput) {
      personId
      sentEmailAddresses
    }
  }
`;

export const GET_RANKINGS_AND_RATINGS = gql`
  query RankingsAndRatings($csTournamentEventId: UUID!) {
    rankingsAndRatings(csTournamentEventId: $csTournamentEventId) {
      eventId
      data {
        participantId
        ranking
        rating
      }
    }
  }
`;

export const GET_EVENTS_COSTS = gql`
  query EventCost($eventIds: [UUID!]!, $tournamentId: UUID!, $personCsIds: [UUID!]!) {
    eventCost(eventIds: $eventIds, tournamentId: $tournamentId, personCsIds: $personCsIds) {
      amountPaid
      amountPayableWithEvents
      personCsId
    }
  }
`;

export const GET_TOURNAMENT_URLS = gql`
  query TournamentUrls($clubSparkTournamentId: UUID!) {
    tournamentUrls(clubSparkTournamentId: $clubSparkTournamentId) {
      publicSite
      adminArea
      webInfo
      tournamentSettings
    }
  }
`;

export const GET_PLAYER_PROFILE = gql`
  query Person($personId: UUID!, $tournamentId: UUID!) {
    person(personId: $personId) {
      tournamentInfo(tournamentId: $tournamentId) {
        contactPhoneNumbers
        contactEmailAddresses
        registeredEvents {
          eventId
          ranking
          eventName
        }
      }
      uaid
    }
  }
`;

export const PERSON_EVENT_ELIGIBILITY = gql`
  query PersonEventEligibility($personCsIds: [UUID!]!, $tournamentId: UUID!, $eventId: UUID!) {
    personEventEligibility(personCsIds: $personCsIds, tournamentId: $tournamentId, eventId: $eventId) {
      eligibilityRuleStatuses {
        ruleId
        status
        strictlyEnforce
      }
      eligibilityStatus
    }
  }
`;

export const COMBINED_ELIGIBILITY = gql`
  query CombineEligibility($eventId: UUID!, $participantIdPairs: [[UUID!]!]!) {
    combineParticipantsEligibility(eventId: $eventId, participantIdPairs: $participantIdPairs) {
      participantIdPair
      success
      error
    }
  }
`;

export const LINKABLE_TOURNAMENTS = gql`
  query LinkableTournaments($tournamentId: UUID!) {
    linkableTournaments(tournamentId: $tournamentId) {
      id
      name
      timings {
        startDate
        endDate
      }
      level {
        id
        name
      }
    }
  }
`;

export const IS_USER_ADMIN = gql`
  query IsUserAdminWithTournamentScope($tournamentId: UUID!) {
    isUserAdminWithTournamentScope(tournamentId: $tournamentId)
  }
`;

export const GET_WTN_RATING = gql`
  query WorldTennisNumber($nationalIds: [String!]!) {
    worldTennisNumber(nationalIds: $nationalIds) {
      tennisID
      nationalID
      worldTennisNumbers {
        ratingDate
        source
        confidence
        tennisNumber
        type
        gameZoneLower
        gameZoneUpper
        isRanked
      }
    }
  }
`;

export const GET_TOURNAMENT_POLICIES = gql`
  query TournamentPolicies($clubSparkTournamentId: UUID!) {
    tournamentPolicies(clubSparkTournamentId: $clubSparkTournamentId) {
      seeding
      scoring
      participant
      roundNaming
      positionActions
    }
  }
`;

export const GET_TOURNAMENT_FEATURES = gql`
  query TournamentFeatures($clubSparkTournamentId: UUID!) {
    tournamentFeatures(clubSparkTournamentId: $clubSparkTournamentId) {
      featureId
      enabled
    }
  }
`;

export const GET_LEVEL_CONFIG = gql`
  query TournamentLevelConfig($tournamentId: UUID!) {
    tournamentLevelConfig(clubSparkTournamentId: $tournamentId) {
      allowAddIndividualPlayers
      allowAddPlayersFromTeams
      allowBackdrawMovements
      allowGeneratePlayOffForBackDraws
      allowQualifyingDraws
      allowSplitDraws
      allowTeamSubstitutions
      allowWildcards
      doublesEventFormats
      doublesScoreFormats
      eventFormats
      firstMatchLoserConsolation
      goldSilverBronzeDrawsEnabled
      id
      ntrpEnabled
      playTracker
      rankingsEnabled
      roundRobinPlayoffDrawEventFormats
      requireSafeplayApproval
      scoreFormats
      splitDrawEventFormats
      teamScoreFormats
      wtnRankingsEnabled
      allowRosterSync
      enforceScorecardGender
      enforceScorecardAge
      hideScorecardPDFTeamMatchUpInfo
      hideScorecardPlayerActions
    }
  }
`;

export const SYNC_DUAL_MATCH_ROSTERS = gql`
  mutation SyncRoster($id: ID!) {
    syncDualMatchRosters(id: $id)
  }
`;

export const GET_DRAW_SETTINGS = gql`
  query GetSettings($personId: String, $tournamentId: String) {
    settings(personId: $personId, tournamentId: $tournamentId) {
      tournamentId
      personId
      draw {
        additionalPlayerInformation
        matchDayInformation {
          selectedInformation
          dates
        }
        playerAdornment
        showEntryStatus
      }
      tournamentDeskUI
      virtualTournamentCheckIn
      virtualMatchUpCheckIn
    }
  }
`;

export const UPDATE_DRAW_SETTINGS = gql`
  mutation UpdateSettings($tournamentId: UUID, $personId: UUID, $update: SettingsUpdate!) {
    updateSettings(tournamentId: $tournamentId, personId: $personId, update: $update) {
      tournamentId
      personId
      draw {
        additionalPlayerInformation
        matchDayInformation {
          selectedInformation
          dates
        }
        playerAdornment
        showEntryStatus
      }
      tournamentDeskUI
      virtualTournamentCheckIn
      virtualMatchUpCheckIn
    }
  }
`;

export const GET_TOURNAMENT_ROLES = gql`
  query TournamentRoles($tournamentId: UUID!) {
    tournamentRoles(tournamentId: $tournamentId) {
      activeRole
      isGlobalAdmin
      isReferee
      isTournamentDirector
      isTournamentAssistant
      isOrganisationAdmin
      isRegionalStaffAdmin
      isStaffAdmin
    }
  }
`;

export const GET_ORG_CONFIG = gql`
  query OrgConfigForTournament($tournamentId: ID!) {
    orgConfigForTournament(tournamentId: $tournamentId) {
      resolved {
        organisationId
        configType
        tieFormatTemplates
      }
    }
  }
`;

export const ADD_PLAYER = gql`
  mutation AddTournamentParticipant($tournamentId: UUID!, $personId: UUID!) {
    addTournamentParticipant(tournamentId: $tournamentId, personId: $personId) {
      tods
    }
  }
`;

export const ADD_PLAYERS_FROM_TEAM = gql`
  mutation AddTournamentParticipantsFromTeam($tournamentId: UUID!, $teamId: UUID!, $personIds: [UUID!]!) {
    addTournamentParticipantsFromTeam(tournamentId: $tournamentId, teamId: $teamId, personIds: $personIds) {
      tods
    }
  }
`;

export const ADD_TOURNAMENT_SNAPSHOT = gql`
  mutation Snapshot($tournamentId: UUID!) {
    addTournamentSnapshot(tournamentId: $tournamentId) {
      id
      tournamentId
      createdAt
      downloadLocation
      snapshotName
    }
  }
`;

export const SET_PERSON_TOURNAMENT_CONTACT_INFO = gql`
  mutation setPersonTournamentContactInfo(
    $tournamentId: UUID!
    $personId: UUID!
    $emails: [String!]!
    $phones: [String!]!
  ) {
    setPersonTournamentContactInfo(tournamentId: $tournamentId, personId: $personId, emails: $emails, phones: $phones) {
      contactEmailAddresses
      contactPhoneNumbers
    }
  }
`;

export const ASSIGN_DUAL_MATCH_PLAYERS = gql`
  mutation AssignDualMatchPlayers($input: AssignDualMatchPlayerInput!) {
    assignDualMatchPlayers(input: $input)
  }
`;

export const UNASSIGN_DUAL_MATCH_PLAYER = gql`
  mutation UnAssignDualMatchPlayer($input: UnAssignDualMatchPlayerInput!) {
    unAssignDualMatchPlayer(input: $input)
  }
`;

export const CLEAR_DUAL_MATCH_LINE_UP = gql`
  mutation ClearDualMatchLineup($input: ClearDualMatchLineupInput!) {
    clearDualMatchLineup(input: $input)
  }
`;

export const GET_ORGANISATIONS_BY_IDS = gql`
  query GetOrganisationsByIds($organisationIds: [String!]!) {
    getOrganisationsByIds(organisationIds: $organisationIds) {
      organisationId
      organisationName
      organisationType
      hasChildren
    }
  }
`;
export const GET_ROSTER_MEMBERS = gql`
  query GetRosterMembers($rosterId: String!, $seasonId: String) {
    getRosterMembers(rosterId: $rosterId, seasonId: $seasonId) {
      personId
      standardFamilyName
      standardGivenName
    }
  }
`;
